/**
 * The Campaigns screen's own API client.
 *
 * It does not go through `lib/api.ts` for the reason the Config Studio's does not: that interface is
 * implemented twice — once by the mock adapter that ships the demo and once by `httpApi` — so every
 * method added to it has to be added to both, and several agents are writing in this repository
 * today. Campaigns is a self-contained screen against `/api/v1/campaigns/*`, so its client lives
 * beside it and reuses the one thing it must not duplicate: the session's CSRF token.
 *
 * Author: Danish Khan
 */
import { currentCsrf } from '../lib/httpApi.ts';
import { ApiError } from '../lib/apiTypes.ts';

const BASE = '/api/v1/campaigns';

export type CampaignStatus = 'draft' | 'running' | 'paused' | 'done' | 'cancelled';
export type Weekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export type ParamSpec =
  | { source: 'literal'; value: string }
  | { source: 'contact.name'; fallback?: string }
  | { source: 'contact.city'; fallback?: string }
  | { source: 'lead.fact'; key: string; fallback?: string };

export interface AudienceFilter {
  tiers?: string[];
  cities?: string[];
  tags?: string[];
  consentSources?: string[];
  consentChannels?: string[];
  importIds?: string[];
  contactIds?: string[];
}

export interface SendWindow {
  days: Weekday[];
  start: string;
  end: string;
}

export interface CampaignDefinition {
  name: string;
  templateName: string;
  defaultLanguage: string;
  params: Record<string, ParamSpec>;
  audience: AudienceFilter;
  citySequence: string[];
  dailyCap: number;
  window: SendWindow;
  pacePerSecond: number;
}

export interface Campaign extends CampaignDefinition {
  id: string;
  status: CampaignStatus;
  definitionDigest: string;
  startedDryRunId: string | null;
  createdBy: string;
  startedAt: string | null;
  startedBy: string | null;
  pausedAt: string | null;
  pausedBy: string | null;
  pauseReason: string | null;
  finishedAt: string | null;
  lastTickAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** True only when a dry run of THIS definition exists and is still fresh. Gates the button. */
  startable: boolean;
  /** The sentence the screen shows instead of a disabled button with no explanation. */
  notStartableBecause: string | null;
}

export interface WindowState {
  open: boolean;
  reason: 'open' | 'outside_hours' | 'wrong_day' | 'quiet_hours';
  opensAt: string | null;
  closesAt: string | null;
  timezone: string;
}

export interface DryRunMember {
  contactId: string;
  language: string;
  templateLanguage: string;
  languageChosen: boolean;
  city: string | null;
  tier: string | null;
  paramCount: number;
  costInr: number | null;
}

export interface DryRun {
  id: string;
  campaignId: string;
  definitionDigest: string;
  at: string;
  candidates: number;
  eligible: DryRunMember[];
  excluded: { contactId: string; reason: string; detail: Record<string, unknown> }[];
  counts: Record<string, number>;
  deferrals: Record<string, number>;
  byLanguage: { language: string; templateLanguage: string; template: string; count: number; chosen: number; assumed: number }[];
  cost: {
    inr: number | null;
    micros: number | null;
    todayInr: number | null;
    perMessageInr: number | null;
    unknown: number;
    unknownReasons: Record<string, number>;
  };
  schedule: {
    window: WindowState;
    dailyCap: number;
    sentToday: number;
    remainingToday: number;
    today: number;
    laterDays: number;
    pacePerSecond: number;
    estimatedMinutes: number;
  };
  divergence: string[];
  listTruncated: boolean;
}

export interface Progress {
  campaignId: string;
  status: string;
  startedAt: string | null;
  pausedAt: string | null;
  pausedBy: string | null;
  pauseReason: string | null;
  finishedAt: string | null;
  enqueued: number;
  sent: number;
  failed: number;
  inFlight: number;
  abandoned: number;
  /** Queued, then recalled by a pause or a cancel before the sender claimed them. */
  cancelled: number;
  costInr: number | null;
  costUnknown: number;
  bySide: { ours: number; meta: number };
  byCode: { code: string; side: 'ours' | 'meta'; label: string; n: number }[];
  refusals: { reason: string; n: number }[];
  replies: number;
  leads: number;
  firstSentAt: string | null;
  lastSentAt: string | null;
  remaining: number | null;
  dryRun: { id: string; eligible: number; costInr: number | null; at: string } | null;
}

export interface LastDryRun {
  id: string;
  definitionDigest: string;
  candidates: number;
  eligible: number;
  counts: Record<string, number>;
  deferrals: Record<string, number>;
  costInr: number | null;
  costUnknown: number;
  byLanguage: Record<string, unknown>[];
  divergence: string[];
  /** False when the campaign has been edited since. */
  current: boolean;
  ranBy: string;
  at: string;
}

export interface CampaignDetail {
  campaign: Campaign;
  lastDryRun: LastDryRun | null;
  progress: Progress | null;
}

export interface Facets {
  tiers: { value: string; n: number }[];
  cities: { value: string; n: number }[];
  tags: { value: string; n: number }[];
  consentSources: { value: string; n: number }[];
  languages: { value: string; n: number }[];
  tenantLanguages: string[];
  timezone: string;
  quietHours: { start: string; end: string };
  caps: {
    maxPacePerSecond: number;
    /**
     * The ceiling the API will actually honour on a campaign's daily cap — the sending number's
     * messaging tier, not the largest tier that exists. It is what the Daily cap field's `max` is.
     */
    maxDailyCap: number;
    marketingPerContactPerDay: number;
    /**
     * Meta's own word for the tier (`TIER_250`), or null when the Graph could not be asked.
     *
     * Null is genuinely "we do not know", never "no limit": the API holds the cap to the entry
     * tier when the read fails, so a null here comes with a `maxDailyCap` that is small for a
     * reason the operator is owed in words.
     */
    messagingTier: string | null;
  };
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const method = init?.method ?? 'GET';
  const headers = new Headers(init?.headers);
  if (method !== 'GET') {
    // Only when there is a body: `createApp` registers a raw JSON parser for the webhook's HMAC and
    // it throws on a bodyless POST that claims `application/json`, answering plain-text 400 before
    // the API router can shape it. Found in the browser, not in a test — see lib/httpApi.ts.
    if (init?.body !== undefined) headers.set('content-type', 'application/json');
    const csrf = currentCsrf();
    if (csrf) headers.set('x-csrf', csrf);
  }
  const res = await fetch(`${BASE}${path}`, { ...init, headers, credentials: 'same-origin' });
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    if (!res.ok) throw new ApiError(text.slice(0, 200) || `${method} ${path} failed`, res.status);
  }
  if (!res.ok) {
    const detail = (body ?? {}) as { error?: { code?: string; message?: string } };
    throw new ApiError(detail.error?.message ?? `${method} ${path} failed`, res.status, {
      ...(detail.error?.code ? { code: detail.error.code } : {}),
    });
  }
  return body as T;
}

const post = (value?: unknown): RequestInit => ({
  method: 'POST',
  ...(value === undefined ? {} : { body: JSON.stringify(value) }),
});

export interface CampaignsClient {
  list(): Promise<Campaign[]>;
  facets(): Promise<Facets>;
  get(id: string): Promise<CampaignDetail>;
  create(d: CampaignDefinition): Promise<Campaign>;
  update(id: string, d: Partial<CampaignDefinition>): Promise<Campaign>;
  dryRun(id: string): Promise<DryRun>;
  start(id: string): Promise<Campaign>;
  pause(id: string, reason: string): Promise<{ campaign: Campaign; progress: Progress }>;
  cancel(id: string, reason: string): Promise<Campaign>;
  results(id: string): Promise<Progress>;
}

export const campaignsApi: CampaignsClient = {
  list: () => call<{ campaigns: Campaign[] }>('').then((r) => r.campaigns),
  facets: () => call<Facets>('/facets'),
  get: (id) => call<CampaignDetail>(`/${encodeURIComponent(id)}`),
  create: (d) => call<{ campaign: Campaign }>('', post(d)).then((r) => r.campaign),
  update: (id, d) => call<{ campaign: Campaign }>(`/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(d) }).then((r) => r.campaign),
  dryRun: (id) => call<{ dryRun: DryRun }>(`/${encodeURIComponent(id)}/dry-run`, post()).then((r) => r.dryRun),
  start: (id) => call<{ campaign: Campaign }>(`/${encodeURIComponent(id)}/start`, post()).then((r) => r.campaign),
  pause: (id, reason) => call<{ campaign: Campaign; progress: Progress }>(`/${encodeURIComponent(id)}/pause`, post({ reason })),
  cancel: (id, reason) => call<{ campaign: Campaign }>(`/${encodeURIComponent(id)}/cancel`, post({ reason })).then((r) => r.campaign),
  results: (id) => call<Progress>(`/${encodeURIComponent(id)}/results`),
};

/* ---------------------------------------------------------------------- shared vocabulary */

/**
 * Every exclusion reason, in a sentence a client can act on.
 *
 * "90 not sent" reads like a bug and gets raised as one. "90 have never given consent for marketing"
 * is a sentence somebody can do something about — which is the entire reason the refusal log exists.
 */
export const EXCLUSION_LABELS: Record<string, string> = {
  no_consent: 'No marketing consent on file',
  consent_withdrawn: 'Withdrew their consent',
  opt_out: 'Asked us to stop',
  deny_list: 'One of our own numbers',
  cooldown: 'Meta stood them down recently',
  paused: 'Asked us to pause — they still get answers',
  marketing_cap: 'Already had a marketing template from us today',
  no_template_in_language: 'No approved template in their language',
  missing_param: 'A template parameter has no value for them',
  // Not about the contact at all: the campaign's own parameter mapping does not fit the body
  // Meta approved in THAT contact's language. The full sentence — which language, what Meta
  // approved, what this campaign maps — is carried in the dry run's `divergence` notes and
  // rendered as prose above Start; this is the counts panel's short name for the same event,
  // and it is the same word the refusals log files it under after a send.
  param_mismatch: 'Our parameters do not fit the body Meta approved in their language',
  daily_cap: "Over today's cap — they go on the next day",
  outside_window: 'Outside the send window — they go when it opens',
  window_closed: 'The 24-hour service window was shut',
  no_consent_source: 'Imported with no provenance for their consent',
};

export const exclusionLabel = (reason: string): string => EXCLUSION_LABELS[reason] ?? reason.replace(/_/g, ' ');

/** Rupees, as an invoice would show them. `null` is "we could not work it out", never "free". */
export function rupees(inr: number | null): string {
  if (inr === null) return 'unknown';
  return `₹${inr.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Why the Daily cap field stops where it does, said in an operator's words.
 *
 * The builder used to print the reduced maximum and nothing else, and an operator who types 5,000
 * into a field that clamps at 250 concludes either that the form is broken or that we capped their
 * account on a whim. Both conclusions arrive as a support call. Naming Meta's own word for the tier
 * is what makes the number checkable: it is the same string that appears on the number in Business
 * Manager, so the person reading this sentence can go and look at it.
 *
 * The tier word and the ceiling are deliberately two separate sentences rather than one causal
 * claim. The API keeps a tier word it does not recognise — Meta may add a tier tomorrow — and holds
 * such a number to the entry ceiling; writing "TIER_5K, so at most 250 a day" would present our own
 * caution as Meta's rule. Two facts, each true on its own, cannot say that.
 */
export function messagingTierNote(caps: Facets['caps']): string {
  const ceiling = `${caps.maxDailyCap.toLocaleString('en-IN')} new customers in a rolling 24 hours`;
  if (caps.messagingTier === null) {
    return `This number's messaging tier is unknown — Meta did not answer when we asked for it.`
      + ` Until it does, the cap is held to ${ceiling}, the tier a verified number starts on,`
      + ` because a read that failed is not evidence of a bigger allowance.`;
  }
  return `This number's messaging tier is ${caps.messagingTier}.`
    + ` The most this cap may be is ${ceiling}: Meta refuses the rest one contact at a time, and`
    + ` each refusal stands that contact down for a day or more.`;
}

export const WINDOW_REASONS: Record<WindowState['reason'], string> = {
  open: 'open',
  outside_hours: 'outside the send window',
  wrong_day: 'not a sending day',
  quiet_hours: "inside this tenant's quiet hours",
};
